import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import Styled from 'styled';

import { URLBuilderLink } from 'lib/url/url-builder-link';
import LinkView from 'lib/ui/link-view';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import { ThreadViewModel } from 'four-nets/forum/category/thread-row';
import { HideForVendor } from 'four-nets/vendor/permissions';
import Tabs from 'four-nets/ui-v2/tabs/tabs';
import NewThreadView, { NewThreadViewModel } from 'four-nets/forum/thread/new-thread/new-thread-view';
import { Site } from 'four-nets/site';

import ForumWidgetItemView from './forum-widget-item-view';
import defaultStyles from './forum-widget-view.scss';

const SECTIONS = {
    ACTIVE: 'active',
    RECENT: 'recent',
};

const FORUM_WIDGET_THREAD_COUNT = {
    101: 13,
    102: 13,
    301: 13,
    302: 13,
    401: 8,
};

export class ForumWidgetViewModel {
    @observable activeSection: SECTIONS = SECTIONS.ACTIVE;

    @observable mostActiveThreads: PagedResult<Thread>;
    @observable mostActiveThreadViewModels: ThreadViewModel[] = [];
    @observable loadingMostActive = false;
    @observable mostActiveError;

    @observable mostRecentThreads: PagedResult<Thread>;
    @observable mostRecentThreadViewModels: ThreadViewModel[] = [];
    @observable loadingMostRecent = false;
    @observable mostRecentError;

    constructor(dataSource) {
        this.dataSource = dataSource;
        this.site = dataSource.categoryStore.userStore.site;

        this.newThreadViewModel = new NewThreadViewModel({
            categoryStore: this.dataSource.categoryStore,
            userStore: this.dataSource.categoryStore.userStore,
        });
        this.forumLink = new URLBuilderLink({
            url: 'forum',
        });
    }

    toJSON() {
        if (this.activeSection == SECTIONS.ACTIVE) {
            return {
                threads: this.mostActiveThreads,
            };
        }
        return {
            threads: this.mostRecentThreads,
        };
    }

    fromJSON(json) {
        const threads = this.dataSource.categoryStore.threadStore.processPagedJSON({
            json: json.threads,
        });

        if (this.activeSection == SECTIONS.ACTIVE) {
            this.loadMostActiveDidSucceed(threads);
        } else {
            this.loadMostRecentDidSucceed(threads);
        }
    }

    @action loadMostActiveDidSucceed = mostActive => {
        this.mostActiveThreads = mostActive;
        this.mostActiveThreadViewModels.replace(mostActive.results.map(thread => new ThreadViewModel({ thread })));
    };

    @action loadMostActiveDidFinish = () => {
        this.loadingMostActive = false;
    };

    @action loadMostActiveDidFail = err => {
        this.mostActiveError = err;
        throw err;
    };

    loadMostActive = () => {
        this.loadingMostActive = true;
        this.mostActiveError = null;

        return this.dataSource.categoryStore.threadStore
            .loadMostActive({ limit: FORUM_WIDGET_THREAD_COUNT[this.site.id] })
            .then(this.loadMostActiveDidSucceed)
            .catch(this.loadMostActiveDidFail)
            .finally(this.loadMostActiveDidFinish);
    };

    @action loadMostRecentDidSucceed = mostRecent => {
        this.mostRecentThreads = mostRecent;
        this.mostRecentThreadViewModels.replace(mostRecent.results.map(thread => new ThreadViewModel({ thread })));
    };

    @action loadMostRecentDidFinish = () => {
        this.loadingMostRecent = false;
    };

    @action loadMostRecentDidFail = err => {
        this.mostRecentError = err;
        throw err;
    };

    loadMostRecent = () => {
        this.loadingMostRecent = true;
        this.mostRecentError = null;
        return this.dataSource.categoryStore.threadStore
            .loadMostRecent({ limit: FORUM_WIDGET_THREAD_COUNT[this.site.id] })
            .then(this.loadMostRecentDidSucceed)
            .catch(this.loadMostRecentDidFail)
            .finally(this.loadMostRecentDidFinish);
    };

    load() {
        return this.loadMostActive();
    }

    @action changeActiveSection = section => {
        if (section == SECTIONS.RECENT) {
            this.activeSection = SECTIONS.RECENT;
            if (!this.mostRecentThreadViewModels.length) {
                this.loadMostRecent().catchReturn();
            }
        } else {
            this.activeSection = SECTIONS.ACTIVE;
            if (!this.mostActiveThreadViewModels.length) {
                this.loadMostActive().catchReturn();
            }
        }
    };

    @computed get activeThreadViewModels() {
        if (this.activeSection == SECTIONS.RECENT) {
            return this.mostRecentThreadViewModels;
        }

        return this.mostActiveThreadViewModels;
    }

    @computed get activeError() {
        if (this.activeSection == SECTIONS.RECENT) {
            return this.mostRecentError;
        }

        return this.mostActiveError;
    }

    @computed get activeLoading() {
        if (this.activeSection == SECTIONS.RECENT) {
            return this.loadingMostRecent;
        }

        return this.loadingMostActive;
    }

    @computed get activeRetry() {
        if (this.activeSection == SECTIONS.RECENT) {
            return () => this.loadMostRecent().catchReturn();
        }

        return () => this.loadMostActive().catchReturn();
    }
}

export class ForumPromoWidgetViewModel {
    @observable threads: PagedResult<Thread>;
    @observable threadViewModels: ThreadViewModel[] = [];
    @observable loading = false;
    @observable error = null;

    constructor(dataSource) {
        this.dataSource = dataSource;
    }

    @action loadPromoDidSucceed = threads => {
        this.threads = threads;
        this.threadViewModels.replace(threads.results.map(thread => new ThreadViewModel({ thread })));
        return threads;
    };

    @action loadPromoDidFinish = () => {
        this.loading = false;
    };

    @action loadPromoDidFail = err => {
        this.error = err;
        throw err;
    };

    toJSON() {
        return { threads: this.threads };
    }

    fromJSON(json) {
        const threads = this.dataSource.categoryStore.threadStore.processPagedJSON({
            json: json.threads,
        });

        this.loadPromoDidSucceed(threads);
    }

    loadPromo = () => {
        this.loading = true;
        this.error = null;
        return this.dataSource.categoryStore.threadStore
            .loadPromo({ limit: 2 })
            .then(this.loadPromoDidSucceed)
            .catch(this.loadPromoDidFail)
            .finally(this.loadPromoDidFinish);
    };

    load = () => {
        return this.loadPromo();
    };

    retry = () => {
        this.loadPromo().catchReturn();
    };
}

export { ForumPromoWidgetView };

@Styled(defaultStyles)
@observer
class ForumPromoWidgetView extends React.Component {
    render() {
        const { styles, forumPromoWidgetViewModel } = this.props;
        return (
            <LoadingWrapperView
                error={forumPromoWidgetViewModel.error}
                loading={forumPromoWidgetViewModel.loading}
                retry={forumPromoWidgetViewModel.retry}
            >
                {forumPromoWidgetViewModel.loading || forumPromoWidgetViewModel.threadViewModels.length === 0 ? null : (
                    <div className={styles.promoWidget}>
                        <Link to='/forum/category/dni-s-heluz' className={styles.link}>
                            <h2 className={styles.promoHeading}>
                                <span>Dni s</span>
                                <img className={styles.logo} src='https://cdn.4nets.sk/dni-s-heluz/heluz-logo.png' />
                            </h2>
                        </Link>
                        <ul className={styles.list}>
                            {forumPromoWidgetViewModel.threadViewModels.map(threadViewModel => (
                                <ForumWidgetItemView
                                    hideHeader={true}
                                    threadViewModel={threadViewModel}
                                    key={threadViewModel.thread.id}
                                />
                            ))}
                        </ul>
                    </div>
                )}
            </LoadingWrapperView>
        );
    }
}

@Styled(defaultStyles)
@observer
class ForumWidgetView extends React.Component {
    newThreadView;

    static contextTypes = {
        authCheck: PropTypes.func,
        site: PropTypes.instanceOf(Site),
    };

    setNewThreadView = (ref: ?NewThreadView) => {
        this.newThreadView = ref;
    };

    onNewThreadClick = () => {
        const { newThreadView } = this;

        if (this.context.site.isGarden === false) {
            window.location.pathname = '/forum/nova-diskusia/';

            return;
        }

        if (newThreadView) {
            const showNewThreadView = () => {
                newThreadView.show({
                    form: this.props.forumWidgetViewModel.newThreadViewModel.setupForm(),
                });
                return Promise.resolve();
            };
            this.context.authCheck(showNewThreadView, AuthOrigin.NewThread);
        }
    };

    changeActiveSection = section => () => {
        this.props.forumWidgetViewModel.changeActiveSection(section);
    };

    render() {
        const { forumWidgetViewModel, styles, hideHeader } = this.props;

        return (
            <div className={styles.forumWidget}>
                {!hideHeader ? (
                    <div className={styles.title}>
                        <a href={forumWidgetViewModel.forumLink.href} className={styles.link}>
                            <h2 className={styles.heading}>
                                <FormattedMessage id='FORUM' />
                            </h2>
                        </a>
                        <HideForVendor>
                            <span>
                                <BigButtonView
                                    icon={newArticle}
                                    label={
                                        <span className={styles.buttonLabel}>
                                            <FormattedMessage id='NEW_THREAD_BUTTON' />
                                        </span>
                                    }
                                    onClick={this.onNewThreadClick}
                                />
                            </span>
                        </HideForVendor>
                    </div>
                ) : null}
                <Tabs
                    tabs={[
                        {
                            onClick: this.changeActiveSection(SECTIONS.ACTIVE),
                            active: forumWidgetViewModel.activeSection === SECTIONS.ACTIVE,
                            child: <FormattedMessage id='FORUM_ACTIVE_DISCUSSIONS' />,
                        },
                        {
                            onClick: this.changeActiveSection(SECTIONS.RECENT),
                            active: forumWidgetViewModel.activeSection === SECTIONS.RECENT,
                            child: <FormattedMessage id='FORUM_LATEST_DISCUSSIONS' />,
                        },
                    ]}
                />
                <LoadingWrapperView
                    error={forumWidgetViewModel.activeError}
                    loading={forumWidgetViewModel.activeLoading}
                    retry={forumWidgetViewModel.activeRetry}
                >
                    <ul className={styles.list}>
                        {forumWidgetViewModel.activeThreadViewModels.map(threadViewModel => (
                            <ForumWidgetItemView
                                hideHeader={true}
                                threadViewModel={threadViewModel}
                                key={threadViewModel.thread.id}
                            />
                        ))}
                    </ul>
                </LoadingWrapperView>
                <NewThreadView
                    innerRef={this.setNewThreadView}
                    newThreadViewModel={forumWidgetViewModel.newThreadViewModel}
                />
            </div>
        );
    }
}

export default ForumWidgetView;
